/**
 * Every file the portal hands the browser goes through here: server
 * exports (CSV, PDF, audio) and files built from data already on screen.
 * One link-click download, one `Content-Disposition` reader, one CSV
 * writer, so every file behaves the same.
 */

import { axiosInstance } from "@/lib/api-client"

type Cell = string | number | boolean | null | undefined

/** A cell Excel would run as a formula (`=`, `@`, `+`, `-`, tab, CR). */
const FORMULA_START = /^[=@+\-\t\r]/
/** A plain number or phone (`+15551234567`, `-3.5`, `(555) 123-4567`). */
const NUMERIC = /^[+-]?[\d\s().-]+$/

/**
 * One CSV cell (RFC 4180). A value that would start a formula gets a
 * leading `'` so a spreadsheet shows it as text, except plain numbers and
 * phones, which stay as they are.
 */
function cell(value: Cell): string {
  let text = value == null ? "" : String(value)
  // Only a leading sign is excused, and only for a plain number or phone.
  if (FORMULA_START.test(text) && !(/^[+-]/.test(text) && NUMERIC.test(text))) {
    text = `'${text}`
  }
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/**
 * Rows (header first) → a CSV document: UTF-8 with a BOM so Excel reads
 * non-ASCII names right, CRLF line endings, quoted where needed.
 */
export function toCsv(rows: Cell[][]): string {
  return "﻿" + rows.map((r) => r.map(cell).join(",")).join("\r\n")
}

/**
 * Hands a blob to the browser as a download. The link is in the DOM for
 * the click (some browsers ignore a detached one) and the URL is freed a
 * second later — revoking at once can cancel the download.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}

/** Builds a CSV from rows (header first) and downloads it. */
export function downloadCsv(rows: Cell[][], filename: string): void {
  saveBlob(new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" }), filename)
}

/** Downloads plain text as a `.txt` (or whatever `filename` says). */
export function downloadText(text: string, filename: string): void {
  saveBlob(new Blob([text], { type: "text/plain;charset=utf-8" }), filename)
}

/**
 * The filename a server response asks for (`Content-Disposition`), or
 * `fallback` when it sends none. RFC 5987 `filename*=UTF-8''…` wins over
 * the plain form when both are there.
 */
export function filenameFromDisposition(
  header: unknown,
  fallback: string
): string {
  if (typeof header !== "string" || !header) return fallback
  const encoded = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header)
  if (encoded) {
    try {
      const name = decodeURIComponent(encoded[1].trim())
      if (name) return name
    } catch {
      // Malformed escape — fall through to the plain form.
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header)
  const name = (plain?.[2] ?? plain?.[1])?.trim()
  return name || fallback
}

/**
 * Shows a blob in a browser tab. Pass the tab opened synchronously inside
 * the click (`window.open("", "_blank")`) — opening one after an await is
 * blocked as a popup. The tab is cut off from this page (`opener`) before
 * it loads anything.
 */
export function showBlob(blob: Blob, tab: Window | null): void {
  const url = URL.createObjectURL(blob)
  if (tab) {
    tab.opener = null
    tab.location.href = url
  } else {
    window.open(url, "_blank", "noopener")
  }
  // The tab has loaded the document long before this; then free the blob.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/** What a server export said about itself beyond the file. */
export type ExportResult = {
  /** The server stopped at its row cap; the file isn't everything. */
  truncated: boolean
  /** That cap, when the server names it. */
  rowLimit: number | null
}

/**
 * A server-built export (`…/export`): fetched with the session cookie,
 * saved under the name the server gives it. The server streams it, and
 * says in `X-Export-Truncated` / `X-Export-Row-Limit` when it hit its
 * row cap, so the caller can tell the user the file was cut short.
 */
export async function downloadExport(
  url: string,
  params: URLSearchParams | Record<string, unknown>,
  fallbackName: string
): Promise<ExportResult> {
  const res = await axiosInstance.get<Blob>(url, {
    params,
    responseType: "blob",
  })
  saveBlob(
    res.data,
    filenameFromDisposition(res.headers["content-disposition"], fallbackName)
  )
  const limit = Number(res.headers["x-export-row-limit"])
  return {
    truncated: String(res.headers["x-export-truncated"] ?? "") === "true",
    rowLimit: Number.isFinite(limit) && limit > 0 ? limit : null,
  }
}

/** The line to show when an export came back cut short, else undefined. */
export function truncationNote(result: ExportResult): string | undefined {
  if (!result.truncated) return undefined
  const cap = result.rowLimit
    ? `the first ${result.rowLimit.toLocaleString("en-US")} rows`
    : "part of the rows"
  return `The file holds ${cap}. Narrow the filters to export the rest.`
}
